import type { LoginResponse } from '@/bindings/LoginResponse'
import { ApiError, refreshSession } from '@/lib/api'
import { type AuthState, type System, type UserOf, getAuth, setAuthenticated } from '@/lib/auth-store'

const inflight: Partial<Record<System, Promise<void>>> = {}

/** Resolve "are we logged in?" once per system: first ask `…/me`, then trust the store. */
export async function ensureAuth<S extends System>(system: S): Promise<AuthState<S>> {
  if (getAuth(system).status === 'unknown') {
    inflight[system] ??= refreshSession(system)
      .catch((err: unknown) => {
        // A 401 already cleared the store; anything else stays "unknown" so
        // the next navigation asks again instead of bouncing to login page.
        if (!(err instanceof ApiError) || err.status !== 401) throw err
      })
      .finally(() => {
        delete inflight[system]
      })
    await inflight[system]
  }
  return getAuth(system)
}

/** Store a login/verify/reset response; the cookie was set by the response itself. */
export function completeLogin<S extends System>(system: S, login: LoginResponse<UserOf<S>>) {
  setAuthenticated(system, {
    user: login.user,
    csrfToken: login.csrf_token,
  })
}
